import { computed, readonly, ref } from 'vue';
import type { CommunityUser } from '@garmoth/community-contracts';
import { ApiError, startDiscordSignIn } from '../api/client';
import { api } from '../api/endpoints';

/**
 * Discord identity for the whole site. The session cookie lives on the API;
 * this store only records what `/api/me` reported.
 *
 * `unknown` is the state before the first answer, so nothing renders a
 * signed-out shell while the session is still being resolved.
 */
export type SessionStatus = 'unknown' | 'signed-in' | 'signed-out' | 'unreachable';

const user = ref<CommunityUser | null>(null);
const status = ref<SessionStatus>('unknown');
const error = ref<string | null>(null);
let pending: Promise<void> | null = null;

async function load(): Promise<void> {
  pending ??= (async () => {
    try {
      user.value = await api.me();
      status.value = 'signed-in';
      error.value = null;
    } catch (cause) {
      user.value = null;
      if (cause instanceof ApiError && cause.isUnauthorized) {
        status.value = 'signed-out';
        error.value = null;
      } else {
        status.value = 'unreachable';
        error.value = cause instanceof ApiError ? cause.message : 'The community API is not reachable.';
      }
    } finally {
      pending = null;
    }
  })();
  return pending;
}

async function signOut(): Promise<void> {
  try {
    await api.signOut();
  } finally {
    user.value = null;
    status.value = 'signed-out';
  }
}

/** Keeps the header in sync after the profile page changes a setting. */
function apply(next: CommunityUser) {
  user.value = next;
}

export function useSession() {
  return {
    user: readonly(user),
    status: readonly(status),
    error: readonly(error),
    isSignedIn: computed(() => status.value === 'signed-in'),
    isResolved: computed(() => status.value !== 'unknown'),
    load,
    apply,
    signIn: startDiscordSignIn,
    signOut,
  };
}
